import {
  Lock as LockIcon,
  MoreHoriz as MoreHorizIcon,
  ViewWeek,
} from '@mui/icons-material';
import GridViewIcon from '@mui/icons-material/GridView';
import { Chip } from '@mui/material';
import React, { MouseEvent, useState } from 'react';
import { AssemblyLayout } from 'src/components/assemblyLayout';
import { AssemblyScreen } from 'src/components/assemblyScreen';
import { AssemblyScreenSection } from 'src/components/assemblyScreenSection';
import { generateGridLayout } from 'src/components/generateGridLayout/GenerateGridLayout';
import { GenericCard } from 'src/components/genericCard';
import { LayoutSectionTypesChip } from 'src/components/layout/chip/LayoutSectionTypesChip';
import { PreviewModal, SimpleDialog } from 'src/components/modal';
import { Snackbar } from 'src/components/snackbar';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { LAYOUT_CARD_WIDTH } from 'src/constants';
import { CodJourney } from 'src/constants/assemblyJourneys';
import { LayoutMenu } from 'src/features/assemblies/layouts/listLayoutsPage/modules/layoutMenu/LayoutMenu';
import {
  useDeleteLayoutVariation,
  useGetAllLayoutType,
  useMenu,
  useModal,
} from 'src/hooks';
import {
  IResponseType,
  IStatus,
  IStatusLabel,
  SectionTypes,
} from 'src/interfaces';
import {
  getResponsiveScale,
  getScreenConfigurationLabel,
  lang,
  useUserRole,
} from 'src/utils';

import { CardContainerBox, CardMenuIcon } from '../Card.styled';
import { IStatusColor, LayoutListCardProps } from './LayoutListCard.interface';
import {
  CardMediaBox,
  CardMediaWrapper,
  DescContainerBox,
  LayoutChipContainer,
  LayoutInfoContainer,
  LayoutText,
  LayoutTitle,
} from './LayoutListCard.styled';

// Derived from a constant enum — defined outside component to avoid re-creation on every render
const animatedJourneyStates = Object.values(CodJourney).filter(
  (journey) => journey !== CodJourney.CUSTOMER_ENTERS_DRIVE_THRU,
) as CodJourney[];

export const LayoutListCard = (props: LayoutListCardProps) => {
  const removeLayoutVariation = useDeleteLayoutVariation();

  const [activeAssemblyJourney, setActiveAssemblyJourney] =
    React.useState<CodJourney>(CodJourney.CUSTOMER_ENTERS_DRIVE_THRU);
  const [hoveredScreenIndex, setHoveredScreenIndex] = React.useState<
    number | null
  >(null);
  const [deletingStatus, setDeletingStatus] = useState<{
    [key: number]: boolean;
  }>({});
  const deleteLayoutModal = useModal();
  const layoutInfoModal = useModal();

  const menu = useMenu();
  const { isViewOnly } = useUserRole();

  const { data: layoutTypes = [] } = useGetAllLayoutType();

  React.useEffect(() => {
    if (hoveredScreenIndex === null) return;

    const interval = setInterval(() => {
      setActiveAssemblyJourney((prevJourneyState) => {
        const currentIndex = animatedJourneyStates.indexOf(prevJourneyState);
        const nextState =
          animatedJourneyStates[
            (currentIndex + 1) % animatedJourneyStates.length
          ];

        if (nextState === CodJourney.PURCHASE_IS_CONFIRMED) {
          clearInterval(interval);
        }

        return nextState;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [hoveredScreenIndex]);

  const onMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    menu.onOpenMenu(event.currentTarget);
  };

  const handleDeleteLayout = async () => {
    setDeletingStatus((prevStatus) => ({
      ...prevStatus,
      [props.layout.id]: true,
    }));
    await removeLayoutVariation.mutateAsync(Number(props.layout.id));
    deleteLayoutModal.onCloseModal();
    Snackbar.success(`${props.layout.name} ${lang.successfullyDeleted}`);
  };

  const isCodConfirm =
    !!props.layout.config?.sectionTypes?.some(
      (event: string) => event === SectionTypes.CONFIRMATION,
    ) && activeAssemblyJourney === CodJourney.PURCHASE_IS_CONFIRMED;

  const handleScreenMouseEnter = (screenIndex: number) => {
    const screen = props.layout.screens[screenIndex];
    const hasDynamicSection = screen?.sections?.some(
      (section) => (section.config?.dynamicHighlight ?? 0) > 0,
    );

    if (!hasDynamicSection) return;

    setHoveredScreenIndex(screenIndex);
    setActiveAssemblyJourney(CodJourney.ORDER_IS_MADE);
  };

  const handleScreenMouseLeave = () => {
    setHoveredScreenIndex(null);
    setActiveAssemblyJourney(CodJourney.CUSTOMER_ENTERS_DRIVE_THRU);
  };

  const scale = getResponsiveScale(
    LAYOUT_CARD_WIDTH,
    props.layout.screens,
    IResponseType.CARD,
  );

  const cardMedia = (
    <CardMediaBox>
      {!isViewOnly && props.layout.status === IStatus.DRAFT && (
        <CardMenuIcon
          $hasVisible={Boolean(menu.anchorEl)}
          onClick={onMenuClick}>
          <MoreHorizIcon />
        </CardMenuIcon>
      )}
      <CardMediaWrapper>
        <AssemblyLayout scale={scale} type="grid">
          {props.layout.screens.map((screen, index) => (
            <AssemblyScreen
              key={screen.name + index}
              height={screen.height}
              width={screen.width}
              scale={scale}
              selectable={false}
              onMouseEnter={() => handleScreenMouseEnter(index)}
              onMouseLeave={handleScreenMouseLeave}
              type="grid">
              {screen.name.includes('placeholder')
                ? generateGridLayout(
                    props.layout?.config?.gridLines?.[index] ?? '',
                    scale,
                  )
                : screen.sections.map((section, index) => (
                    <AssemblyScreenSection
                      key={section.name + index}
                      section={section}
                      sectionTypes={props.layout.config?.sectionTypes}
                      activeAssemblyJourney={activeAssemblyJourney}
                      isCodConfirmationOpen={isCodConfirm}
                      scale={scale}
                      selectable={false}
                      showEditIcon={false}
                      type="grid"
                    />
                  ))}
            </AssemblyScreen>
          ))}
        </AssemblyLayout>
      </CardMediaWrapper>
    </CardMediaBox>
  );

  const additionalContent = (
    <DescContainerBox>
      <LayoutChipContainer>
        {props.layout.status === IStatus.READY && <LockIcon />}
        <Chip
          label={
            <>
              {props.layout.status
                ? IStatusLabel[props.layout.status]
                : props.layout.status}
            </>
          }
          color={
            props.layout.status
              ? IStatusColor[props.layout.status]
              : props.layout.status
          }
          variant="outlined"
          size="small"
        />
      </LayoutChipContainer>

      <CustomTooltip
        width={300}
        placement="bottom"
        title={props.layout.name}
        overflowLineNumber={2}>
        <LayoutTitle>{props.layout.name}</LayoutTitle>
      </CustomTooltip>

      <LayoutInfoContainer>
        <GridViewIcon />
        <LayoutText>
          {
            layoutTypes.find((type) => type.id === props.layout.layout_type_id)
              ?.name
          }
        </LayoutText>
      </LayoutInfoContainer>
      <LayoutInfoContainer>
        <ViewWeek />
        <LayoutText>
          {getScreenConfigurationLabel(props.layout.screens)}
        </LayoutText>
      </LayoutInfoContainer>
      <LayoutInfoContainer>
        <LayoutSectionTypesChip
          sectionTypes={props?.layout?.config?.sectionTypes}
        />
      </LayoutInfoContainer>
    </DescContainerBox>
  );

  return (
    <CardContainerBox>
      <GenericCard
        onClick={layoutInfoModal.onOpenModal}
        cardMedia={cardMedia}
        additionalContent={additionalContent}
        cardMenuItems={
          !isViewOnly && (
            <LayoutMenu
              anchorEl={menu.anchorEl}
              onClose={menu.onCloseMenu}
              card={props.layout}
              onDeleteClick={deleteLayoutModal.onOpenModal}
            />
          )
        }
      />
      {layoutInfoModal.open && (
        <PreviewModal
          open={layoutInfoModal.open}
          onClose={layoutInfoModal.onCloseModal}
          type="layout"
          content={props.layout}
        />
      )}
      <SimpleDialog
        actionColor="error"
        open={deleteLayoutModal.open}
        cancelText={lang.cancel}
        confirmText={lang.deleteText}
        title={lang.deleteLayoutModal.title}
        subTitle={lang.deleteLayoutModal.subTitle}
        onClose={deleteLayoutModal.onCloseModal}
        onClickCancel={deleteLayoutModal.onCloseModal}
        modalSize="sm"
        disabled={deletingStatus[props.layout.id]}
        onClickConfirm={handleDeleteLayout}
      />
    </CardContainerBox>
  );
};
